/** UX-flow components — Blueprint Part 6.
 *
 *  Three reusable patterns that show up across the whole subscriber app:
 *    • <UpgradeLock>   — blur+lock overlay on premium features (6.4)
 *    • <FirstRunCard>  — one-next-action card on /app/dashboard (6.1)
 *    • <ConfidenceWrap> — every audience number wrapped with a popover badge (6.3)
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { Button, Pill } from "@/design";
import { color, radius, type, font, elevation } from "@/design/tokens";
import { api } from "@/lib/api";

// ── UpgradeLock ────────────────────────────────────────────────────────────
/**  Wraps any content with a blur + lock overlay. Renders the REAL component
 *   underneath so users see what they'll get — Stripe-style "show the value,
 *   then gate." Unlock copy is precise: "Pro unlocks full age × LSM × region
 *   — GH₵7,000/mo".
 */
const PLAN_UNLOCK_COPY = {
  starter: { name: "Starter", ghs: 1800, audience: "station" },
  pro:     { name: "Pro",     ghs: 7000, audience: "station" },
  agency:  { name: "Agency",  ghs: 15000, audience: "agency" },
};
export function UpgradeLock({ needed = "pro", feature = "this view", children, "data-testid": tid }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const { formatGHS } = useCurrency();
  const plan = PLAN_UNLOCK_COPY[needed] || PLAN_UNLOCK_COPY.pro;
  // Admin & users whose plan rank already covers the feature → render unlocked
  const ranks = { free: 0, starter: 1, pro: 2, agency: 3, admin: 99 };
  if (user && (ranks[user.plan_id || "free"] >= ranks[needed] || user.role === "admin")) {
    return <>{children}</>;
  }
  return (
    <div data-testid={tid || "upgrade-lock"} style={{ position: "relative" }}>
      {/* Blurred real content underneath */}
      <div style={{ filter: "blur(6px) saturate(0.6)", opacity: 0.55, pointerEvents: "none", userSelect: "none" }}>
        {children}
      </div>
      {/* Lock overlay */}
      <div style={{
        position: "absolute", inset: 0, display: "flex", alignItems: "center",
        justifyContent: "center", background: "rgba(10,22,40,0.35)",
        backdropFilter: "blur(2px)", borderRadius: radius.lg, zIndex: 5,
      }}>
        <div data-testid="upgrade-lock-card" style={{
          background: "#fff", border: `1px solid ${color.border}`,
          borderRadius: radius.lg, padding: "22px 26px", boxShadow: elevation.e3,
          maxWidth: 440, textAlign: "center",
        }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>🔒</div>
          <div style={{ ...type.h3, color: color.ink, marginBottom: 4 }}>
            {plan.name} unlocks {feature}
          </div>
          <div style={{ ...type.small, color: color.slate500, marginBottom: 14 }}>
            <span style={{ ...type.number, color: color.audience[plan.audience] || color.cyan600, fontSize: 16 }}>
              {formatGHS(plan.ghs)}
            </span>
            <span> /month</span>
            <Pill variant="accent" data-testid="upgrade-lock-plan-badge"
              style={{ marginLeft: 8 }}>{plan.name}</Pill>
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
            <Button data-testid="upgrade-lock-cta" variant="primary" audience={plan.audience}
              onClick={() => nav("/pricing")}>
              See full pricing →
            </Button>
            <Button variant="ghost" size="md" onClick={() => nav("/methodology")}>
              How we measure
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── FirstRunCard ───────────────────────────────────────────────────────────
/**  Reads the existing onboarding endpoint and renders ONE next-action as a
 *   full-width guided card. Dismissed automatically when 100% complete.
 */
const STEP_COPY = {
  claim:     { label: "Claim your station", href: "/claim", cta: "Claim now" },
  rate_card: { label: "Add your rate card", href: "/app/dashboard?tab=rate_card", cta: "Open rate-card builder" },
  schedule:  { label: "Set your programme schedule", href: "/app/dashboard?tab=schedule", cta: "Build schedule" },
  profile:   { label: "Complete your public station profile", href: "/app/dashboard?tab=profile", cta: "Finish profile" },
  ad_log:    { label: "Upload your first ad log", href: "/app/dashboard?tab=ad_log", cta: "Upload CSV" },
};
export function FirstRunCard({ stationId, "data-testid": tid }) {
  const [data, setData] = useState(null);
  const nav = useNavigate();
  useEffect(() => {
    if (!stationId) return;
    api.get(`/stations/${stationId}/onboarding-status`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [stationId]);
  if (!data || data.completion_pct >= 100) return null;
  // API returns steps as a list of {id, label, done, tab, impact}
  const nextStep = (data.steps || []).find((s) => !s.done);
  if (!nextStep) return null;
  const copy = STEP_COPY[nextStep.id] || { label: nextStep.label, href: "/app/dashboard", cta: "Open" };
  const stationName = data.station_name || "your station";

  return (
    <div data-testid={tid || "first-run-card"} style={{
      background: `linear-gradient(135deg, ${color.audience.station}, ${color.cyan400})`,
      borderRadius: radius.lg, padding: "20px 26px",
      display: "flex", justifyContent: "space-between", alignItems: "center", gap: 18,
      marginBottom: 24, flexWrap: "wrap",
      boxShadow: `0 10px 32px ${color.audience.station}33`,
      fontFamily: font.sans, color: "#fff",
    }}>
      <div style={{ flex: 1, minWidth: 260 }}>
        <div style={{ ...type.micro, color: "#E0F2FE", marginBottom: 4 }}>
          NEXT STEP · {data.completion_pct}% complete
        </div>
        <h3 style={{ ...type.h3, color: "#fff", margin: 0, fontWeight: 700 }}>
          You&apos;ve claimed {stationName}.{" "}
          <span style={{ color: "#FEF3C7" }}>{copy.label} →</span>
        </h3>
        <div style={{
          marginTop: 10, height: 6, background: "rgba(255,255,255,0.25)",
          borderRadius: 999, overflow: "hidden", maxWidth: 480,
        }}>
          <div style={{
            width: `${data.completion_pct}%`, height: "100%",
            background: "#FBBF24", transition: "width 320ms ease-out",
          }} />
        </div>
      </div>
      <Button data-testid="first-run-cta" variant="primary" size="lg"
        onClick={() => nav(copy.href)}
        style={{ background: "#fff", color: color.audience.station, borderColor: "#fff" }}>
        {copy.cta} →
      </Button>
    </div>
  );
}
